/**
 * TanStack Query mutation hooks for reporter operations
 *
 * Replaces manual axios calls with:
 * - Automatic loading/error states (isPending, isError)
 * - Cache invalidation on success
 * - Request cancellation
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { getApiBaseUrl } from "../config/api";
import { studyKeys } from "./useStudyQueries";
import type { JSONContent } from "@tiptap/react";

import type { ReporterAiMode } from "../atoms/reporterPrefsAtoms";

// ============================================================================
// Types
// ============================================================================

export interface SaveReportStateParams {
  readerScheduleId?: string;
  expectedRevision?: string;
  operationId?: string;
  userId: string;
  studyId: string;
  structuredEditorState?: JSONContent;
  reportExamType?: string;
  prelimEditorState?: JSONContent;
}

export interface SignReportParams {
  userId: string;
  studyId: string;
  finalReportHtml: string;
  finalReportText: string;
  /** A/B reporter arm captured at sign time so the row is self-describing. */
  reporterAiMode?: ReporterAiMode;
  /** Strip-mode only — count of explicit Accept clicks during the session. */
  acceptedFindingsCount?: number;
  /** Strip-mode only — count of explicit Dismiss clicks during the session. */
  dismissedFindingsCount?: number;
  /** Both arms — count of successful voice-tune (` / ⌃`) invocations. */
  voiceTuneCount?: number;
}

export interface UnsignReportParams {
  userId: string;
  studyId: string;
}

export interface SubmitReaderFeedbackParams {
  userId: string;
  studyId: string;
  readerConfidence: number;
  mentalDemand: number;
}

export interface ResetStudyStateParams {
  adminUserId: string;
  targetUserId: string;
  studyId: string;
}

// ============================================================================
// API Functions
// ============================================================================

async function saveReportState(
  token: string,
  params: SaveReportStateParams,
): Promise<{ status: number; message?: string; report_revision?: string }> {
  const payload: Record<string, any> = {
    user_id: params.userId,
    study_id: params.studyId,
  };

  if (params.readerScheduleId) payload.reader_schedule_id = params.readerScheduleId;
  if (params.expectedRevision !== undefined) payload.expected_revision = params.expectedRevision;
  if (params.operationId) payload.operation_id = params.operationId;

  if (params.structuredEditorState) {
    payload.structured_editor_state = params.structuredEditorState;
  }
  if (params.reportExamType) {
    payload.report_exam_type = params.reportExamType;
  }
  if (params.prelimEditorState) {
    payload.prelim_editor_state = params.prelimEditorState;
  }

  const response = await axios.post(`${getApiBaseUrl()}/api/save_report_state`, payload, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (response.data?.status !== 200) {
    throw new Error(response.data?.message || "Failed to save report state");
  }

  return response.data;
}

async function signReport(
  token: string,
  params: SignReportParams,
): Promise<{ status: number; message?: string }> {
  // Only stamp arm + counts when supplied — the BE treats absent fields as
  // "don't overwrite" so a stale client can't blank out a freshly-stamped row.
  const body: Record<string, unknown> = {
    user_id: params.userId,
    study_id: params.studyId,
    final_report_html: params.finalReportHtml,
    final_report_text: params.finalReportText,
  };
  if (params.reporterAiMode !== undefined) body.reporter_ai_mode = params.reporterAiMode;
  if (params.acceptedFindingsCount !== undefined)
    body.accepted_findings_count = params.acceptedFindingsCount;
  if (params.dismissedFindingsCount !== undefined)
    body.dismissed_findings_count = params.dismissedFindingsCount;
  if (params.voiceTuneCount !== undefined) body.voice_tune_count = params.voiceTuneCount;

  const response = await axios.post(`${getApiBaseUrl()}/api/sign_report`, body, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (response.data?.status !== 200) {
    throw new Error(response.data?.message || "Failed to sign report");
  }

  return response.data;
}

async function unsignReport(
  token: string,
  params: UnsignReportParams,
): Promise<{ status: number; message?: string }> {
  const response = await axios.post(
    `${getApiBaseUrl()}/api/unsign_report`,
    {
      user_id: params.userId,
      study_id: params.studyId,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );

  if (response.data?.status !== 200) {
    throw new Error(response.data?.message || "Failed to unsign report");
  }

  return response.data;
}

async function submitReaderFeedback(
  token: string,
  params: SubmitReaderFeedbackParams,
): Promise<{ status: number; message?: string }> {
  const response = await axios.post(
    `${getApiBaseUrl()}/api/submit_reader_feedback`,
    {
      user_id: params.userId,
      study_id: params.studyId,
      reader_confidence: params.readerConfidence,
      mental_demand: params.mentalDemand,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );

  if (response.data?.status !== 200) {
    throw new Error(response.data?.message || "Failed to submit reader feedback");
  }

  return response.data;
}

async function resetStudyState(
  token: string,
  params: ResetStudyStateParams,
): Promise<{ status: number; message?: string }> {
  const response = await axios.post(
    `${getApiBaseUrl()}/api/reset_study_state`,
    {
      admin_user_id: params.adminUserId,
      target_user_id: params.targetUserId,
      study_id: params.studyId,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );

  if (response.data?.status !== 200) {
    throw new Error(response.data?.message || "Failed to reset study state");
  }

  return response.data;
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Save report state
 * Replaces direct axios call in ReporterEditorPanel.tsx
 */
export function useSaveReportState(token: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: SaveReportStateParams) => {
      if (!token) {
        return Promise.reject(new Error("No authentication token"));
      }
      return saveReportState(token, params);
    },
    onSuccess: (_, variables) => {
      // The open reader editor owns its revision. Refetching on each save
      // would replace its baseline while newer edits are still unsaved.
      if (variables.readerScheduleId) return;
      // Invalidate report state cache (mutable data - always refetch)
      queryClient.invalidateQueries({
        queryKey: studyKeys.studyReportState(variables.userId, variables.studyId),
      });
    },
  });
}

/**
 * Sign report
 * Replaces direct axios call in ReporterEditorPanel.tsx
 */
export function useSignReport(token: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: SignReportParams) => {
      if (!token) {
        return Promise.reject(new Error("No authentication token"));
      }
      return signReport(token, params);
    },
    onSuccess: (_, variables) => {
      // Invalidate report state cache to reflect signed status
      queryClient.invalidateQueries({
        queryKey: studyKeys.studyReportState(variables.userId, variables.studyId),
      });
    },
  });
}

/**
 * Unsign report — flips is_report_signed back to false.
 * Preserves final_report_html / final_report_text as audit trail.
 */
export function useUnsignReport(token: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: UnsignReportParams) => {
      if (!token) {
        return Promise.reject(new Error("No authentication token"));
      }
      return unsignReport(token, params);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: studyKeys.studyReportState(variables.userId, variables.studyId),
      });
    },
  });
}

/**
 * Submit reader feedback
 * Replaces direct axios call in ReporterEditorPanel.tsx
 */
export function useSubmitReaderFeedback(token: string | null) {
  return useMutation({
    mutationFn: (params: SubmitReaderFeedbackParams) => {
      if (!token) {
        return Promise.reject(new Error("No authentication token"));
      }
      return submitReaderFeedback(token, params);
    },
  });
}

/**
 * Reset study state (admin only)
 * Replaces direct axios call in ReporterEditorPanel.tsx
 */
export function useResetStudyState(token: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: ResetStudyStateParams) => {
      if (!token) {
        return Promise.reject(new Error("No authentication token"));
      }
      return resetStudyState(token, params);
    },
    onSuccess: (_, variables) => {
      // Invalidate all study caches
      queryClient.invalidateQueries({
        queryKey: studyKeys.studyReportState(variables.targetUserId, variables.studyId),
      });
      queryClient.invalidateQueries({
        queryKey: studyKeys.studyModelPrediction(variables.targetUserId, variables.studyId),
      });
    },
  });
}
